import useClientSearch from '@hooks/query/clients/useClientSearch';
import { Modal, useIsAuthorized } from '@ui';
import React from 'react';
import { TAuthority } from 'types/authority.types';
import { IClientSearchItem, TClientPairingStatus } from 'types/client.types';
import PairingModal from './components/pairing-modal';
import TableFilter from './components/table-filter';
import { TSearchValues } from './components/table-filter/table-filter';
import { TableSC } from './ProviderPatients.styled';
import {
  INITIAL_SEARCH_PARAMS,
  INITIAL_TABLE_STATE,
  TTableProps,
  useColumns,
} from './ProviderPatients.utils';

const EDIT_AUTH: TAuthority[] = ['clientManagementEdit'];

const ProviderPatients: React.FunctionComponent = () => {
  const [searchParams, setSearchParams] = React.useState(INITIAL_SEARCH_PARAMS);
  const canEdit = useIsAuthorized(EDIT_AUTH);

  const { data, isFetching, refetch } = useClientSearch(searchParams, {
    keepPreviousData: true,
  });

  const handleTableChange = React.useCallback<TTableProps['onChange']>((pagination, sorters) => {
    let sortBy: string | undefined;
    let sortDirection: 'ASC' | 'DESC' | undefined;

    if (sorters.length > 0) {
      sortBy = sorters[0].id;
      sortDirection = sorters[0].desc ? 'DESC' : 'ASC';
    }

    setSearchParams((o) => ({
      ...o,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sortBy: sortBy as any,
      direction: sortDirection,
    }));
  }, []);

  const handleFilter = React.useCallback((params: TSearchValues) => {
    setSearchParams((o) => ({
      ...o,
      page: 1,
      name: params.query,
      status: params.status === 'all' ? undefined : params.status,
    }));
  }, []);

  const {
    open: openPairing,
    close: closePairing,
    data: pairingData,
    modalProps,
  } = Modal.useModal<{ mode: TClientPairingStatus; client: IClientSearchItem }>();

  const openPairingModal = React.useCallback(
    (
      e: React.MouseEvent<Element, MouseEvent>,
      client: IClientSearchItem,
      status: TClientPairingStatus,
    ) => {
      openPairing(e, { client, mode: status });
    },
    [],
  );

  const columns = useColumns({ onPairClick: openPairingModal, canEdit });

  return (
    <>
      <TableSC.Header
        title={`Pacienti (${data?.pagination.total ?? 0})`}
        extra={<TableFilter refetch={refetch} onParamsChange={handleFilter} canEdit={canEdit} />}
        style={{ flexWrap: 'wrap' }}
      />
      <TableSC
        manualPagination
        manualSortBy
        columns={columns}
        loading={isFetching}
        data={data?.items ?? []}
        showEmpty
        pagination={data?.pagination}
        emptyTitle={'Žiadni pacienti'}
        onChange={handleTableChange}
        initialState={INITIAL_TABLE_STATE}
      />
      <PairingModal {...modalProps} {...pairingData} afterSubmit={refetch} onClose={closePairing} />
    </>
  );
};

export default React.memo(ProviderPatients);
